/**
 * AI Assistant Module - English Translations
 */
export const aiAssistantEn = {
  // General
  title: 'AI Assistant',
  description: 'Intelligent Q&A assistant for IT, HR, Admin questions',
  
  // Floating Action Button
  fab: {
    tooltip: 'AI Assistant',
    open: 'Open Assistant',
    close: 'Close Assistant',
  },

  // Chat Dialog
  chat: {
    title: 'AI Assistant',
    placeholder: 'Type your question...',
    send: 'Send',
    sending: 'Sending...',
    newChat: 'New Chat',
    history: 'History',
    thinking: 'AI is thinking...',
    error: 'Sorry, an error occurred. Please try again',
    retry: 'Retry',
    empty: 'Start a new conversation by typing your question',
    welcome: 'Hello! I\'m your enterprise AI assistant. I can help you with IT, HR, and administrative questions. How can I help you today?',
    disclaimer: '* AI provides guidance only and does not perform any system operations',
  },

  // Messages
  message: {
    you: 'You',
    assistant: 'AI Assistant',
    system: 'System',
    copied: 'Copied to clipboard',
    copy: 'Copy',
    regenerate: 'Regenerate',
  },

  // Feedback
  feedback: {
    helpful: 'Helpful',
    notHelpful: 'Not Helpful',
    thanks: 'Thanks for your feedback!',
    addComment: 'Add comment (optional)',
    submit: 'Submit Feedback',
    submitted: 'Feedback submitted',
  },

  // Escalation
  escalate: {
    button: 'Get Human Help',
    title: 'Request Human Assistance',
    description: 'Would you like to escalate this issue to human support?',
    confirm: 'Confirm Escalation',
    cancel: 'Cancel',
    category: 'Category',
    priority: 'Priority',
    additionalInfo: 'Additional Information',
    additionalInfoPlaceholder: 'Please provide more details (optional)',
    success: 'Ticket created successfully. Our team will contact you soon',
    ticketNumber: 'Ticket Number',
    categories: {
      IT: 'IT Support',
      HR: 'Human Resources',
      ADMIN: 'Administrative',
      FINANCE: 'Finance',
      OTHER: 'Other',
    },
    priorities: {
      LOW: 'Low',
      MEDIUM: 'Medium',
      HIGH: 'High',
      URGENT: 'Urgent',
    },
  },

  // Knowledge Fix
  knowledgeFix: {
    button: 'Report Incorrect',
    title: 'Correct Answer',
    description: 'If the AI answer is inaccurate, please provide the correct answer',
    correctAnswer: 'Correct Answer',
    correctAnswerPlaceholder: 'Enter the correct answer...',
    submit: 'Submit Correction',
    success: 'Thank you! Your correction has been submitted for review',
    cancel: 'Cancel',
  },

  // History
  history: {
    title: 'Conversation History',
    empty: 'No conversation history',
    loadMore: 'Load More',
    delete: 'Delete',
    deleteConfirm: 'Are you sure you want to delete this conversation?',
    deleted: 'Conversation deleted',
    today: 'Today',
    yesterday: 'Yesterday',
    earlier: 'Earlier',
    searchPlaceholder: 'Search conversations...',
  },

  // Status
  status: {
    ACTIVE: 'Active',
    CLOSED: 'Closed',
    ESCALATED: 'Escalated',
  },

  // Categories
  categories: {
    GENERAL: 'General',
    IT: 'IT Support',
    HR: 'Human Resources',
    ADMIN: 'Administrative',
    FINANCE: 'Finance',
  },

  // Admin
  admin: {
    title: 'AI Assistant Management',
    dashboard: 'Dashboard',
    prompts: 'Prompt Management',
    tickets: 'Ticket Management',
    knowledge: 'Knowledge Management',
    config: 'Configuration',
    stats: 'Statistics',
  },

  // Dashboard
  dashboard: {
    title: 'AI Assistant Dashboard',
    callsLast24h: 'Calls (24h)',
    callsLast7d: 'Calls (7d)',
    successRate: 'Success Rate',
    avgResponseTime: 'Avg Response Time',
    categoryDistribution: 'Category Distribution',
    satisfactionRate: 'Satisfaction Rate',
    hallucinations: 'Hallucination Reports',
    pendingTickets: 'Pending Tickets',
    seconds: 's',
  },

  // Prompts
  prompts: {
    title: 'Prompt Template Management',
    description: 'Manage system prompts for different categories',
    create: 'Create Template',
    edit: 'Edit Template',
    name: 'Template Name',
    category: 'Category',
    content: 'Template Content',
    version: 'Version',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    activate: 'Activate',
    preview: 'Preview',
    save: 'Save',
    delete: 'Delete',
    deleteConfirm: 'Are you sure you want to delete this template?',
    emptyTitle: 'No prompt templates',
    emptyDesc: 'Create a prompt template to get started',
    dialogCreateTitle: 'Create Template',
    dialogEditTitle: 'Edit Template',
    namePlaceholder: 'Enter template name',
    contentPlaceholder: 'Enter system prompt content...',
    cancel: 'Cancel',
    actions: 'Actions',
    activateAction: 'Activate',
    editAction: 'Edit',
    deleteAction: 'Delete',
  },

  // Tickets
  tickets: {
    title: 'Ticket Management',
    id: 'Ticket ID',
    category: 'Category',
    priority: 'Priority',
    status: 'Status',
    assignee: 'Assignee',
    createdAt: 'Created At',
    actions: 'Actions',
    view: 'View',
    assign: 'Assign',
    resolve: 'Resolve',
    resolution: 'Resolution',
    resolutionPlaceholder: 'Enter resolution...',
    statuses: {
      PENDING: 'Pending',
      IN_PROGRESS: 'In Progress',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
    },
  },

  // Knowledge
  knowledge: {
    title: 'Knowledge Correction Management',
    description: 'Review and approve knowledge corrections from users',
    originalAnswer: 'Original Answer',
    correctAnswer: 'Correct Answer',
    contributor: 'Contributor',
    status: 'Status',
    reviewNote: 'Review Note',
    approve: 'Approve',
    reject: 'Reject',
    view: 'View',
    createdAt: 'Created At',
    filterAll: 'All',
    emptyTitle: 'No knowledge corrections',
    emptyDesc: 'There are no corrections to review',
    dialogTitle: 'Knowledge Correction Details',
    close: 'Close',
    reviewNoteOptional: 'Review Note (Optional)',
    reviewNotePlaceholder: 'Add a note...',
    statuses: {
      PENDING: 'Pending Review',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
    },
  },

  // Stats
  stats: {
    title: 'Usage Statistics',
    overview: 'Overview',
    feedback: 'Feedback Analysis',
    hallucinations: 'Hallucination Analysis',
    dateRange: 'Date Range',
    totalConversations: 'Total Conversations',
    totalMessages: 'Total Messages',
    totalTickets: 'Total Tickets',
    resolutionRate: 'Resolution Rate',
    satisfactionScore: 'Satisfaction Score',
    likes: 'Likes',
    dislikes: 'Dislikes',
    likeRate: 'Like Rate',
  },

  // Config
  config: {
    title: 'System Configuration',
    pageDescription: 'Configure AI assistant behavior and limits',
    key: 'Config Key',
    value: 'Config Value',
    description: 'Description',
    save: 'Save',
    saved: 'Configuration saved',
    emptyTitle: 'No configurations',
    emptyDesc: 'There are no configurable items available',
    actions: 'Actions',
    edit: 'Edit',
    cancel: 'Cancel',
    descriptions: {
      max_tokens: 'Maximum tokens per response',
      temperature: 'Response creativity (0-1)',
      model: 'LLM model to use',
      rate_limit: 'Max requests per minute per user',
      max_context_messages: 'Max messages to include in context',
      enable_streaming: 'Enable streaming responses',
      enable_pii_detection: 'Enable PII detection and masking',
      enable_prompt_guard: 'Enable prompt injection protection',
    },
  },

  // Errors
  errors: {
    conversationNotFound: 'Conversation not found',
    messageTooLong: 'Message content is too long',
    rateLimit: 'Too many requests. Please try again later',
    serviceUnavailable: 'Service temporarily unavailable. Please try again later',
    networkError: 'Network error. Please check your connection',
  },
};

export default aiAssistantEn;
